import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Calendar,
  MessageCircle,
  TrendingUp,
  Clock,
  CheckCircle,
  User,
  FileText,
  X
} from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS } from '@constants/admin.constants';
import apiService from '@services/api/api.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const StudentDetails = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingReport, setViewingReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_STUDENTS)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchStudentDetails();
  }, [studentId]);

  const fetchStudentDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/admin/students/${studentId}/details`);
      if (response.success) {
        setStudent(response?.data?.data);
      }
    } catch (error) {
      toast.error('Failed to load student details');
      navigate('/admin/students');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (reportId) => {
    setReportLoading(true);
    try {
      const response = await apiService.get(`/career-reports/${reportId}`);
      if (response.success) {
        setViewingReport(response?.data?.data);
      }
    } catch (error) {
      toast.error('Failed to load report details');
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportViewer = () => {
    setViewingReport(null);
  };

  if (loading || !student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_STUDENTS}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Scrollable Content */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-6">
            {/* Back Button */}
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/students')}
                className="hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Students
              </Button>
            </div>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 animate-fadeIn">
              <StatsCard
                title="Questions Asked"
                value={student.stats?.totalQuestions || 0}
                icon={MessageCircle}
                color="primary"
              />
              <StatsCard
                title="Answered"
                value={student.stats?.answeredQuestions || 0}
                icon={CheckCircle}
                color="green"
              />
              <StatsCard
                title="Pending"
                value={student.stats?.pendingQuestions || 0}
                icon={Clock}
                color="yellow"
              />
              <StatsCard
                title="Career Reports"
                value={student.careerReports?.length || 0}
                icon={FileText}
                color="purple"
              />
              <StatsCard
                title="Active Days"
                value={student.stats?.activeDays || 0}
                icon={TrendingUp}
                color="blue"
              />
            </div>

            {/* Student Info */}
            <Card className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  {student.profilePicture ? (
                    <img
                      src={`${student.profilePicture}`}
                      alt={`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email}
                      className="w-20 h-20 rounded-2xl object-cover shadow-lg transform hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg transform hover:scale-105 transition-transform duration-300">
                      {student.firstName?.[0]?.toUpperCase() || student.lastName?.[0]?.toUpperCase() || student.email?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-primary-900">
                      {`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-primary-500 font-semibold uppercase tracking-wide">Email</p>
                    <p className="text-sm font-medium text-primary-900 truncate">{student.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-primary-500 font-semibold uppercase tracking-wide">Phone</p>
                    <p className="text-sm font-medium text-primary-900 truncate">{student.phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-primary-500 font-semibold uppercase tracking-wide">Joined</p>
                    <p className="text-sm font-medium text-primary-900">
                      {new Date(student.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Recent Questions */}
            <Card className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary-900 flex items-center gap-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  Recent Questions
                </h3>
                {student.recentQuestions && student.recentQuestions.length > 0 && (
                  <span className="text-sm text-primary-500 font-medium">
                    {student.recentQuestions.length} questions
                  </span>
                )}
              </div>
              {student.recentQuestions && student.recentQuestions.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {student.recentQuestions.map((question, index) => (
                    <div 
                      key={question.id} 
                      className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-sm font-medium text-primary-900 flex-1 leading-relaxed">
                          {question.questionText}
                        </p>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ml-4 whitespace-nowrap ${
                          question.status === 'answered' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : question.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                          {question.status}
                        </span>
                      </div>
                      {question.answerText && (
                        <div className="mt-3 p-4 bg-white rounded-lg border border-blue-100 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <p className="text-xs text-primary-500 font-semibold uppercase tracking-wide">Answer</p>
                          </div>
                          <p className="text-sm text-primary-900 leading-relaxed">{question.answerText}</p>
                          {question.mentorEmail && (
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                              <User className="w-3 h-3 text-primary-400" />
                              <p className="text-xs text-primary-500">
                                By: <span className="font-medium text-primary-700">{question.mentorEmail}</span>
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-primary-400" />
                          <span className="text-xs text-primary-500">
                            Asked: {new Date(question.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {question.answeredAt && (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="text-xs text-green-600 font-medium">
                              Answered: {new Date(question.answeredAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-primary-500">No questions asked yet</p>
                </div>
              )}
            </Card>

            {/* Career Reports */}
            <Card className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary-900 flex items-center gap-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  Generated Career Reports
                </h3>
                {student.careerReports && student.careerReports.length > 0 && (
                  <span className="text-sm text-primary-500 font-medium">
                    {student.careerReports.length} reports
                  </span>
                )}
              </div>
              {student.careerReports && student.careerReports.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {student.careerReports.map((report, index) => (
                    <div 
                      key={report.id} 
                      className="p-4 bg-gradient-to-r from-purple-50 to-white rounded-xl border border-purple-200 hover:border-purple-300 hover:shadow-md transition-all duration-200"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-primary-900 mb-1">
                            {report.title || 'Career Path Report'}
                          </h4>
                          {report.description && (
                            <p className="text-xs text-primary-600 leading-relaxed">
                              {report.description}
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ml-4 whitespace-nowrap ${
                          report.status === 'completed' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : report.status === 'processing'
                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                          {report.status || 'completed'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-purple-100">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-primary-400" />
                          <span className="text-xs text-primary-500">
                            Generated: {new Date(report.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {report.careerPath && (
                          <div className="flex items-center gap-1.5">
                            <TrendingUp className="w-3 h-3 text-purple-500" />
                            <span className="text-xs text-purple-600 font-medium">
                              {report.careerPath}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewReport(report.id)}
                          disabled={reportLoading}
                          className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Report
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-primary-500">No career reports generated yet</p>
                </div>
              )}
            </Card>

            {/* Activity Timeline */}
            <Card className="p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary-900 flex items-center gap-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  Activity Timeline
                </h3>
                {student.activityLog && student.activityLog.length > 0 && (
                  <span className="text-sm text-primary-500 font-medium">
                    {student.activityLog.length} activities
                  </span>
                )}
              </div>
              {student.activityLog && student.activityLog.length > 0 ? (
                <div className="relative max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                  <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-200 via-primary-300 to-transparent" />
                  <div className="space-y-4 relative">
                    {student.activityLog.map((activity, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-4 group"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <div className="relative">
                          <div className="w-4 h-4 rounded-full bg-primary-500 border-4 border-white shadow-md group-hover:scale-125 transition-transform duration-200 flex-shrink-0" />
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 group-hover:border-primary-300 group-hover:shadow-md transition-all duration-200">
                            <p className="text-sm font-medium text-primary-900">{activity.action}</p>
                            <p className="text-xs text-primary-500 mt-1.5 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-primary-500">No activity recorded</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Career Report Viewer Modal */}
      {viewingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {viewingReport.reportName || 'Career Report'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeReportViewer}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
              {/* Personal Information */}
              {viewingReport.data?.careerStage && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-red-600" />
                    Personal Information
                  </h4>
                  <p className="text-sm text-primary-900">
                    Your name is <strong>{student.firstName} {student.lastName}</strong>. You are a <strong className="capitalize">{viewingReport.profileType}</strong> currently building your career foundation.
                  </p>
                </div>
              )}

              {/* Education */}
              {viewingReport.data?.selectedCourse && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Education
                  </h4>
                  <p className="text-sm text-primary-900 mb-2">
                    You are pursuing <strong>Course ID: {viewingReport.data.selectedCourse}</strong> for a duration of <strong>{viewingReport.data.courseDuration || 'N/A'}</strong>.
                  </p>
                  {viewingReport.data.cgpaRange && (
                    <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm font-semibold text-yellow-800">📊 Safe Zone</p>
                      <p className="text-xs text-yellow-700 mt-1">Your CGPA is good. CGPA: {viewingReport.data.cgpaRange}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Location */}
              {viewingReport.data?.fresherState && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    Location
                  </h4>
                  <p className="text-sm text-primary-900">
                    You are Campus location based in <strong>{viewingReport.data.fresherDistrict}, {viewingReport.data.fresherState}</strong> at <strong>{viewingReport.data.fresherAddress}</strong>.
                  </p>
                </div>
              )}

              {/* Personality Traits */}
              {viewingReport.data?.selectedPersonalityTraits?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    Personality Traits
                  </h4>
                  <p className="text-sm text-primary-900">
                    You exhibit traits like <strong>{viewingReport.data.selectedPersonalityTraits.map(t => t.name || t).join(', ')}</strong>.
                  </p>
                </div>
              )}

              {/* Selected Exams */}
              {viewingReport.data?.selectedExams?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-red-600" />
                    Selected Exams ({viewingReport.data.selectedExams.length})
                  </h4>
                  <p className="text-sm text-primary-900 mb-3">
                    You plan to appear for the following exams to advance your career:
                  </p>
                  <div className="space-y-3">
                    {viewingReport.data.selectedExams.map((exam, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <h5 className="font-bold text-primary-900">{exam.examName}</h5>
                        <p className="text-xs text-red-600 mt-1">{exam.category}</p>
                        <p className="text-sm text-primary-700 mt-2"><strong>Purpose:</strong> {exam.purpose}</p>
                        <p className="text-sm text-primary-700"><strong>Eligibility:</strong> {exam.eligibility}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aptitude Preparation */}
              {viewingReport.data?.selectedAptitudeTopics?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-600" />
                    Aptitude Preparation
                  </h4>
                  <p className="text-sm text-primary-900">
                    You are preparing topics like <strong>{viewingReport.data.selectedAptitudeTopics.map(t => t.topic).join(', ')}</strong>.
                  </p>
                </div>
              )}

              {/* Selected Jobs */}
              {viewingReport.data?.selectedJobs?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Career Opportunities
                  </h4>
                  <div className="space-y-3">
                    {viewingReport.data.selectedJobs.map((job, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <h5 className="font-bold text-primary-900">{job.title}</h5>
                        <p className="text-xs text-green-600 mt-1">{job.category} - {job.sector}</p>
                        <p className="text-sm text-primary-700 mt-2"><strong>Skills:</strong> {job.skills?.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Skills */}
              {viewingReport.data?.professionalSoftSkills?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    Professional Soft Skills
                  </h4>
                  <div className="space-y-2">
                    {viewingReport.data.professionalSoftSkills.map((skill, idx) => (
                      <div key={idx} className="p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-sm font-semibold text-primary-900">{skill.skillName}</p>
                        <p className="text-xs text-primary-600">{skill.definition}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hard Skills */}
              {viewingReport.data?.selectedHardSkills?.length > 0 && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="text-lg font-bold text-primary-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                    Hard Skills
                  </h4>
                  <div className="space-y-2">
                    {viewingReport.data.selectedHardSkills.map((skill, idx) => (
                      <div key={idx} className="p-2 bg-purple-50 rounded border border-purple-200">
                        <p className="text-sm font-semibold text-primary-900">{skill.name}</p>
                        <p className="text-xs text-purple-600">{skill.category}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <Button
                variant="outline"
                onClick={closeReportViewer}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </PermissionGuard>
  );
};

export default StudentDetails;
